import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getNavigationRoutes } from '../../routes/routes';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getDefaultWarehouse, getWarehouseNameMap, listWarehouses, setActiveWarehouse } from '../../store/warehouseSlice';
import { fetchActiveShift, clearActiveShift } from '../../store/shiftSlice';
import { getSelectableWarehouses } from '../../utils/warehouseOptions';
import Icon from '../icons/Icon';
import Sidebar from './Sidebar/Sidebar';
import UserMenu from './UserMenu';

// Tailwind replacement for MUI useMediaQuery
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);
  return matches;
};

// Pure Tailwind searchable store-switcher — no external library
function StoreSwitcher({ warehouses, active, onSelect, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const label = active ? (active.warehouse_name || active.name) : 'Select store';

  return (
    <div ref={ref} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`flex h-8 items-center gap-2 rounded-lg border px-3 text-sm font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#0061B2]/20 ${
          disabled
            ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500'
            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
        }`}
      >
        <span className={`h-2 w-2 shrink-0 rounded-full ${disabled ? 'bg-slate-400' : 'bg-green-500'}`} aria-hidden />
        <span className="max-w-[160px] truncate">{label}</span>
        {disabled ? (
          <Icon name="lock" className="h-4 w-4 shrink-0 text-slate-400" />
        ) : (
          <Icon
            name="chevrondown"
            className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {/* Dropdown panel */}
      {!disabled && open && (
        <div className="absolute right-0 top-full z-50 mt-1.5 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          {/* Options list */}
          <ul className="max-h-52 overflow-y-auto py-1">
            {warehouses.map((w) => {
              const isActive = active?.name === w.name;
              const displayName = w.warehouse_name || w.name;
              return (
                <li key={w.name}>
                  <button
                    type="button"
                    onClick={() => { onSelect(w); setOpen(false); }}
                    className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors ${
                      isActive
                        ? 'bg-[#0061B2]/5 text-[#0061B2]'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-bold uppercase ${
                      isActive ? 'bg-[#0061B2] text-white' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {displayName.charAt(0)}
                    </span>
                    <span className="flex-1 truncate font-medium">{displayName}</span>
                    {isActive && <Icon name="checkcircle" className="h-4 w-4 shrink-0 text-[#0061B2]" />}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Footer count */}
          <div className="border-t border-slate-100 px-4 py-1.5">
            <p className="text-[10px] text-slate-400">
              {warehouses.length} store{warehouses.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const Layout = ({ children }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const { warehouses, activeWarehouse, defaultWarehouse } = useAppSelector((state) => state.warehouse);
  const { activeShift } = useAppSelector((state) => state.shift);

  const selectableWarehouses = useMemo(() => getSelectableWarehouses(warehouses), [warehouses]);

  // The warehouse picker is always the authority — shifts track assignment info
  // but never override or lock the warehouse the user has selected.
  const isShiftLocked = false;

  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const unreadNotifications = 0;

  const isPOSRoute = location.pathname === '/sales' || location.pathname === '/sales/pos';
  const isDashboardRoute = location.pathname.startsWith('/dashboard');

  const isMobile = useMediaQuery('(max-width: 767px)');

  const userCompany =
    user?.company ||
    user?.custom_company ||
    user?.company_name ||
    user?.company_data?.name ||
    user?.company_data?.company_name;

  useEffect(() => {
    if (userCompany) {
      dispatch(listWarehouses({ company: userCompany, limit: 1000 }));
      dispatch(getDefaultWarehouse({ company: userCompany }));
    }
    // Load the complete id -> friendly name map so every screen (and the
    // pure-function receipt printers) can resolve warehouse ids to names.
    dispatch(getWarehouseNameMap(userCompany ? { company: userCompany } : {}));
  }, [dispatch, userCompany]);

  useEffect(() => {
    if (user?.name) {
      dispatch(fetchActiveShift());
    } else {
      dispatch(clearActiveShift());
    }
  }, [dispatch, user?.name]);


  useEffect(() => {
    if (selectableWarehouses.length > 0 && !activeWarehouse) {
      const userAssigned = user?.default_warehouse
        ? selectableWarehouses.find((w) => w.name === user.default_warehouse)
        : null;
      const warehouseToSet =
        userAssigned ||
        defaultWarehouse ||
        selectableWarehouses.find((w) => w.is_default) ||
        selectableWarehouses[0];
      if (warehouseToSet) dispatch(setActiveWarehouse(warehouseToSet));
    }
  }, [selectableWarehouses, defaultWarehouse, activeWarehouse, user?.default_warehouse, dispatch]);

  if (isPOSRoute) {
    return <div className="h-screen overflow-auto bg-white">{children}</div>;
  }

  return (
    <div className="min-h-screen bg-white h-screen overflow-hidden">
      <div className="app-layout-grid grid h-screen grid-cols-[auto_1fr] overflow-hidden">
        <Sidebar isMobile={isMobile} mobileOpen={mobileOpen} onCloseMobile={() => setMobileOpen(false)} />

        <div className="min-w-0">
          {/* Top nav */}
          <div className="sticky top-0 z-40 bg-white shadow-sm no-print">
            <div className="flex h-12 items-center gap-3 px-4">

              {isMobile && (
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-md p-2 hover:bg-slate-100"
                  onClick={() => setMobileOpen(true)}
                  aria-label="Open sidebar"
                >
                  <Icon name="menu" className="h-5 w-5 text-slate-700" />
                </button>
              )}

              {/* Right-side actions */}
              <div className="ml-auto flex items-center gap-2">

                {/* Store switcher */}
                <StoreSwitcher
                  warehouses={selectableWarehouses}
                  active={activeWarehouse}
                  onSelect={(w) => dispatch(setActiveWarehouse(w))}
                  disabled={isShiftLocked}
                />



                {/* POS */}
                <button
                  type="button"
                  onClick={() => navigate('/sales/pos')}
                  className="hidden h-8 items-center gap-1.5 rounded-lg bg-[#0061B2] px-3 text-sm font-medium text-white transition-colors hover:bg-[#004d8c] md:inline-flex"
                >
                  <Icon name="pointofsale" className="h-4 w-4" />
                  POS
                </button>

                {/* Notifications */}
                <button
                  type="button"
                  className="relative inline-flex items-center justify-center rounded-md p-2 hover:bg-slate-100"
                  aria-label="Notifications"
                >
                  <Icon name="notifications" className="h-5 w-5 text-slate-600" />
                  {unreadNotifications > 0 && (
                    <span className="absolute right-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
                      {unreadNotifications}
                    </span>
                  )}
                </button>

                {/* Settings */}
                <button
                  type="button"
                  onClick={() => navigate('/settings')}
                  className="inline-flex items-center justify-center rounded-md p-2 hover:bg-slate-100"
                  aria-label="Settings"
                >
                  <Icon name="settings" className="h-5 w-5 text-slate-600" />
                </button>

                {/* User avatar */}
                <button
                  type="button"
                  className="ml-1 inline-flex items-center rounded-full hover:ring-2 hover:ring-slate-200"
                  onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                  aria-label="Account menu"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2e9e2e] text-sm font-semibold text-white">
                    {user?.full_name?.charAt(0) || 'U'}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Page content */}
          <div
            className={`app-content-area h-[calc(100vh-48px)] overflow-y-auto px-4 py-2 md:px-6 bg-white ${
              !isDashboardRoute ? 'flat-ui' : ''
            }`}
          >
            {children}
          </div>
        </div>
      </div>

      <UserMenu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={() => setUserMenuAnchor(null)} />
    </div>
  );
};

export default Layout;
